// Explica, con palabras, QUÉ verá y podrá aprobar una persona en la pantalla «Aprobaciones» de Muestras según sus permisos y su
// canal / Product Manager. Los permisos por sí solos no lo dicen: decidir un paso no basta, también hace falta poder VER las
// solicitudes de su canal o categoría (o estar asignado como aprobador). Las reglas son las de cada app (domain/reglas.js de
// Muestras): Tecnoboga asigna aprobadores por solicitud; Vicsa decide por canal / Product Manager.
//
// Devuelve una lista de { tipo: 'ok' | 'aviso' | 'no', texto }.

const ok = (texto) => ({ tipo: 'ok', texto });
const aviso = (texto) => ({ tipo: 'aviso', texto });
const no = (texto) => ({ tipo: 'no', texto });

const nombreDe = (lista, id) => (lista || []).find((x) => Number(x.id) === Number(id))?.nombre || null;

/**
 * @param {string} appCodigo  'muestras' (Tecnoboga) o 'muestras-vicsa'
 * @param {Set<string>|string[]} permisosEfectivos  claves de los permisos que la persona tiene hoy
 * @param {{ canalId?: number, canalIds?: number[], todosCanales?: boolean, pmId?: number, canales?: any[], pms?: any[] }} alcance
 * @param {{ nombre: string, alcance?: { canalIds?: number[], todosCanales?: boolean, pmId?: number } }[]} cubiertos  personas a las que
 *   reemplaza hoy. En Vicsa hereda sus canales y su Product Manager; en Tecnoboga ve lo que les llegaría a ellas.
 */
export function diagnosticoAprobaciones(appCodigo, permisosEfectivos, alcance = {}, cubiertos = []) {
  const permisos = permisosEfectivos instanceof Set ? permisosEfectivos : new Set(permisosEfectivos || []);
  const tiene = (clave) => permisos.has(clave);
  // Canal(es) que cubre: «todos», varios uno por uno o uno solo (formato anterior, solo canalId)
  // En Vicsa quien reemplaza a otra persona hereda sus canales y su Product Manager (se suman a los propios)
  const heredan = appCodigo === 'muestras-vicsa' ? cubiertos || [] : [];
  const propiosCanal = alcance.canalIds?.length ? alcance.canalIds : alcance.canalId ? [alcance.canalId] : [];
  const todosCanales = Boolean(alcance.todosCanales) || heredan.some((c) => c.alcance?.todosCanales);
  const idsCanal = [...new Set([...propiosCanal, ...heredan.flatMap((c) => c.alcance?.canalIds || [])])];
  const nombresCanal = idsCanal.map((id) => nombreDe(alcance.canales, id) || `canal ${id}`);
  const canal = todosCanales ? 'todos los canales' : nombresCanal.length ? nombresCanal.join(', ') : null;
  const delCanal = todosCanales ? 'de todos los canales' : idsCanal.length > 1 ? `de los canales ${canal}` : `del canal ${canal}`;
  const idsPm = [...new Set([alcance.pmId, ...heredan.map((c) => c.alcance?.pmId)].filter(Boolean))];
  const nombresPm = idsPm.map((id) => nombreDe(alcance.pms, id) || `PM ${id}`);
  const pm = nombresPm.length ? nombresPm.join(', ') : null;
  const delPm = idsPm.length > 1 ? `de las categorías ${pm}` : `de su categoría (${pm})`;
  const items = [];

  const pasos = ['aprobacion:paso1', 'aprobacion:paso2', 'aprobacion:paso3'];
  const decideAlgo = pasos.some(tiene) || tiene('aprobacion:cualquier-paso');
  if (!decideAlgo && !tiene('aprobacion:ver-pendientes')) {
    return [no('No participa en las aprobaciones: no tiene permiso para decidir ningún paso ni para ver la bandeja.')];
  }

  if (!tiene('aprobacion:ver-pendientes')) {
    items.push(aviso('No verá la pantalla «Aprobaciones»: falta «Ver su bandeja de solicitudes por decidir», aunque pueda decidir pasos.'));
  }

  if ((cubiertos || []).length > 0) {
    const nombres = cubiertos.map((c) => c.nombre).join(', ');
    if (appCodigo === 'muestras-vicsa') {
      const hereda = [heredan.some((c) => c.alcance?.todosCanales) ? 'todos los canales' : heredan.some((c) => (c.alcance?.canalIds || []).length) ? 'sus canales' : null, heredan.some((c) => c.alcance?.pmId) ? 'su Product Manager' : null].filter(Boolean);
      items.push(hereda.length
        ? ok(`Además cubre a ${nombres}: hereda ${hereda.join(' y ')} y verá y podrá decidir lo que les llegaría a ellos, en los pasos que su propio perfil permite.`)
        : aviso(`Cubre a ${nombres}, pero esa persona no tiene canal ni Product Manager asignado en Vicsa: no hereda nada.`));
    } else {
      items.push(ok(`Además cubre a ${nombres}: verá y podrá decidir lo que les llegaría a ellos, en los pasos que su propio perfil permite.`));
    }
  }

  if (tiene('aprobacion:cualquier-paso')) {
    items.push(ok(appCodigo === 'muestras'
      ? 'Puede decidir cualquier paso de cualquier solicitud, aunque no sea el aprobador asignado (siempre que tenga el permiso de ese paso).'
      : 'Puede decidir los pasos 1 y 2 de cualquier solicitud (canal y Product Manager), sin importar de quién sean.'));
  }

  if (appCodigo === 'muestras' && tiene('aprobacion:cualquier-paso')) {
    // Con «cualquier paso» el alcance por canal / PM / aprobador asignado no aplica: solo importa tener el permiso de cada paso.
    [[1, 'Product Manager'], [2, 'Canal'], [3, 'Final']].forEach(([n, nombre]) => {
      items.push(tiene(`aprobacion:paso${n}`) ? ok(`Paso ${n} (${nombre}): lo decide en cualquier solicitud.`) : no(`Paso ${n} (${nombre}): no puede decidirlo.`));
    });
  } else if (appCodigo === 'muestras') {
    // Tecnoboga: 1 = Product Manager, 2 = Canal, 3 = Final. Cada solicitud trae sus aprobadores asignados (se eligen en Muestras > Usuarios).
    if (!tiene('aprobacion:paso1')) {
      items.push(no('Paso 1 (Product Manager): no puede decidirlo.'));
    } else if (tiene('solicitud:ver-de-su-pm') && pm) {
      items.push(ok(`Paso 1 (Product Manager): decide las solicitudes de su categoría (${pm}) y las que tengan su nombre como Aprobación 1.`));
    } else if (tiene('solicitud:ver-de-su-pm')) {
      items.push(aviso('Paso 1 (Product Manager): tiene «Ver las solicitudes de su categoría» pero no tiene Product Manager asignado arriba, así que no verá las de ninguna categoría. Elige su Product Manager.'));
    } else {
      items.push(aviso('Paso 1 (Product Manager): solo verá las solicitudes donde esté asignado como Aprobación 1. Para ver las de su categoría, activa «Ver las solicitudes de su categoría» y elige su Product Manager.'));
    }

    if (!tiene('aprobacion:paso2')) {
      items.push(no('Paso 2 (Canal): no puede decidirlo.'));
    } else if (tiene('solicitud:ver-de-su-canal') && canal) {
      items.push(ok(`Paso 2 (Canal): decide las solicitudes ${delCanal} y las que tengan su nombre como Aprobación 2 o Aprobación canal. Si el solicitante ya tiene otro aprobador de canal asignado, esa solicitud le llega a esa persona.`));
    } else if (tiene('solicitud:ver-de-su-canal')) {
      items.push(aviso('Paso 2 (Canal): tiene «Ver las solicitudes de su canal» pero no tiene canal asignado arriba, así que no verá las de ningún canal. Elige su Canal.'));
    } else {
      items.push(aviso(`Paso 2 (Canal): puede decidirlo, pero NO podrá ver las solicitudes de su canal${canal ? ` (${canal})` : ''}: solo verá las donde esté asignado como Aprobación 2 o Aprobación canal. Para ver las de su canal, activa «Ver las solicitudes de su canal»${canal ? '' : ' y elige su Canal'}.`));
    }

    if (!tiene('aprobacion:paso3')) {
      items.push(no('Paso 3 (Final): no puede decidirlo.'));
    } else if (tiene('aprobacion:sin-asignar')) {
      items.push(ok('Paso 3 (Final): decide las solicitudes donde esté asignado como Aprobación 3 y las que no tengan aprobador final.'));
    } else {
      items.push(ok('Paso 3 (Final): decide las solicitudes donde esté asignado como Aprobación 3.'));
    }
  } else {
    // Vicsa: 1 = Canal, 2 = Product Manager, 3 = Final. Se decide por canal / PM de la persona, sin aprobadores por solicitud.
    if (!tiene('aprobacion:paso1')) {
      items.push(no('Paso 1 (Canal): no puede decidirlo.'));
    } else if (tiene('aprobacion:cualquier-paso')) {
      items.push(ok('Paso 1 (Canal): puede decidirlo en cualquier canal.'));
    } else if (canal) {
      items.push(ok(`Paso 1 (Canal): decide las solicitudes ${delCanal}.`));
    } else {
      items.push(aviso('Paso 1 (Canal): puede decidirlo, pero no tiene canal asignado arriba, así que no verá ninguna solicitud. Elige su Canal.'));
    }

    if (!tiene('aprobacion:paso2')) {
      items.push(no('Paso 2 (Product Manager): no puede decidirlo.'));
    } else if (tiene('aprobacion:cualquier-paso')) {
      items.push(ok('Paso 2 (Product Manager): puede decidirlo con cualquier Product Manager.'));
    } else if (pm) {
      items.push(ok(`Paso 2 (Product Manager): decide las solicitudes ${delPm}.`));
    } else {
      items.push(aviso('Paso 2 (Product Manager): puede decidirlo, pero no tiene Product Manager asignado arriba, así que no verá ninguna solicitud. Elige su Product Manager.'));
    }

    if (!tiene('aprobacion:paso3')) {
      items.push(no('Paso 3 (Final): no puede decidirlo.'));
    } else {
      items.push(ok('Paso 3 (Final): decide la aprobación final de las solicitudes que lleguen a ese paso.'));
    }
  }

  if (decideAlgo && !tiene('aprobacion:rechazar')) {
    items.push(aviso('Puede aprobar, pero no rechazar: falta «Rechazar con motivo».'));
  }

  return items;
}
